import React, { FunctionComponent, useEffect, useState } from 'react';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import EditIcon from '@mui/icons-material/Edit';
import { InspectionData } from 'types/location.type';
import { fetchLocationInspections, updateLocationInspection } from 'services/location';
import FileItem from '../../../../../components/LrFileItems/components/FileItem';
import LocationInspectionDialog from './LocationInspectionDialog';

interface Props {
    locationId: string;
    locationName: string;
    locationAddress?: string;
}

const useStyles = makeStyles(() => ({
    tableRow: {
        '& .hoverEditCell': {
            display: 'none',
        },
        '&:hover .hoverEditCell': {
            display: 'inline-flex',
        },
    },
    editTableCell: {
        padding: '0px',
        width: '150px',
    },
}));

const LocationInspectionList: FunctionComponent<Props> = (props) => {
    const { locationId, locationName, locationAddress } = props;
    const [inspections, setInspections] = useState<InspectionData[]>([]);
    const [editInspection, setEditInspection] = useState<InspectionData | undefined>(undefined);

    const getLocationInspections = async () => {
        try {
            const data = await fetchLocationInspections(locationId);
            setInspections(data);
        } catch (e) {
            // do nothing
        }
    };

    useEffect(() => {
        getLocationInspections();
    }, [locationId]);

    const classes = useStyles();

    const handleInspectionSave = async (inspectionData: InspectionData) => {
        await updateLocationInspection(locationId, inspectionData);
        const idx = inspections.findIndex((i) => i.id === inspectionData.id);
        if (idx >= 0) {
            inspections[idx] = inspectionData;
            setInspections([...inspections]);
        }
    };

    return (
        <>
            {inspections && (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align={'left'}>Date Requested</TableCell>
                            <TableCell align={'left'}>Date Received</TableCell>
                            <TableCell align={'left'}>Date Paid</TableCell>
                            <TableCell align={'left'}>Most Recent Report</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {inspections.map((row) => (
                            <TableRow key={row.id} className={classes.tableRow}>
                                <TableCell align={'left'}>{row.requestedDate}</TableCell>
                                <TableCell align={'left'}>{row.receivedDate || 'N/A'}</TableCell>
                                <TableCell align={'left'}>{row.paidDate || 'N/A'}</TableCell>
                                <TableCell align={'left'}>
                                    {row.inspectionReport ? (
                                        <FileItem
                                            label={row.inspectionReport.title}
                                            name={row.inspectionReport.fileName}
                                            uuid={row.inspectionReport.uuid}
                                            noMargin
                                        />
                                    ) : (
                                        'N/A'
                                    )}
                                </TableCell>
                                <TableCell align={'right'} className={classes.editTableCell}>
                                    <IconButton
                                        onClick={() => setEditInspection(row)}
                                        className={'hoverEditCell'}
                                        size="large"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <LocationInspectionDialog
                isOpen={!!editInspection}
                onCancel={() => setEditInspection(undefined)}
                onInspectionSave={handleInspectionSave}
                locationName={locationName}
                locationAddress={locationAddress}
                inspectionReportData={editInspection}
            />
        </>
    );
};

export default LocationInspectionList;
